import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { getProspectReference } from "@utils/storage";
import { postSaveAscertain } from "@modules/eirSalesFacade/apis/saveAscertain";
import { IEirSalesSaveAscertainPayload } from "@modules/eirSalesFacade/interfaces/payloads/IEirSalesSaveAscertainPayload";
import { IEirSalesAscertain } from "@modules/eirSalesFacade/interfaces/models/IEirSalesAscertain";
import { useFetchState } from "@hooks/useFetchState";
import { ICart } from "@features/cart/ICart";
import { updateCartWithAscertain } from "@features/cart/utils/updateCartWithAscertain";
import { useAscertain } from "@modules/eirSalesFacade/swr/ascertain";

interface IParams {
    offerId: number;
    setCart: Dispatch<SetStateAction<ICart | undefined>>;
}

export const useSaveAscertain = (props: IParams) => {
    const { offerId, setCart } = props;
    const [ascertain, setAscertain] = useState<IEirSalesAscertain | undefined>(undefined);
    const { startFetching, endFetching, error, isLoading } = useFetchState();
    const prospectReference = getProspectReference();
    const { mutate: mutateAscertain } = useAscertain(offerId, { revalidateOnMount: false });

    const saveAscertain = useCallback(
        async (payload: IEirSalesSaveAscertainPayload, successCallback?: (ascertain: IEirSalesAscertain) => void) => {
            if (!prospectReference) {
                return;
            }

            try {
                startFetching();

                const savedAscertain = await postSaveAscertain(prospectReference, offerId, payload);

                if (savedAscertain) {
                    setAscertain(savedAscertain);
                    updateCartWithAscertain({ ascertain: savedAscertain, setCart });
                    await mutateAscertain(savedAscertain, false);

                    if (successCallback) {
                        successCallback(savedAscertain);
                    }
                }

                endFetching();
            } catch (apiError) {
                endFetching(apiError);
            }
        },
        [endFetching, mutateAscertain, prospectReference, setCart, startFetching]
    );

    return { saveAscertain, error, isLoading, ascertain };
};
